import { faCircle } from "@jewlr/sharp-solid-svg-icons/faCircle"
import { faDiamond } from "@jewlr/sharp-solid-svg-icons/faDiamond"
import { faHeart } from "@jewlr/sharp-solid-svg-icons/faHeart"
import { faSquare } from "@jewlr/sharp-solid-svg-icons/faSquare"
import { Helmet } from "react-helmet-async"

import LandingHeroBanner from "areas/landing/elements/hero-banner"
import LandingFeatureImageBox from "areas/landing/elements/landing-feature-image-box"
import CustomerReviews from "components/landing/customer-reviews"
import OccasionGrid from "components/occasion-grid"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Image from "components/ui/image"
import { jewlr_t } from "helpers/application"
import { getImgSrcSet } from "helpers/image"
import { cn } from "styles/utils"

/* eslint-disable sort-keys */
const heroBannerElement = {
  data: {
    flex_style: { flexDirection: { _: "column", desktop: "row" } },
    items: [
      {
        image: {
          fallback:
            "https://cms.jewlr.com/about_us_hero_banner_bc89633fc2/about_us_hero_banner_bc89633fc2.jpg",
          srcSet: [
            {
              height: 850,
              media: "(max-width: 767px)",
              sizes: "100vw",
              srcSet: getImgSrcSet(
                `${sl.config.jewlr_imgsmbly_url}cms/about_us_hero_banner_m_d8be3d3ee5/about_us_hero_banner_m_d8be3d3ee5.jpg`,
                750
              ),
              width: 750,
            },
            {
              height: 1400,
              media: "(min-width: 768px)",
              sizes: "(min-width: 1024px) 50vw, 100vw",
              srcSet: getImgSrcSet(
                `${sl.config.jewlr_imgsmbly_url}cms/about_us_hero_banner_bc89633fc2/about_us_hero_banner_bc89633fc2.jpg`,
                1920
              ),
              width: 1920,
            },
          ],
        },
      },
      {
        style: {
          backgroundColor: "#F7A719",
        },
        texts: [
          {
            text: `${jewlr_t("Jewelry")} Made <br/>Personal`,
            style: {
              as: "h1",
              mb: { _: 3, desktop: 4 },
              fontSize: {
                _: "42px",
                desktop: "60px",
                large: "70px",
                widescreen: "84px",
              },
              fontWeight: "700",
              lineHeight: 1,
            },
            type: "subtitle",
          },
          {
            text: `Create ${jewlr_t("terms.personalized")} ${jewlr_t("jewelry")} that's as <br/>meaningful to give as it is to wear.`,
            style: {
              fontSize: {
                _: "16px",
                desktop: "20px",
                large: "24px",
                widescreen: "28px",
              },
              fontWeight: "400",
              lineHeight: "124%",
            },
            type: "description",
          },
        ],
        text_container_style: {
          py: 4,
          px: 2,
          color: "blackPrint",
          textAlign: "center",
          alignItems: "center",
          position: "static",
          justifyContent: "center",
        },
      },
    ],
  },
}

const confettiRowData = [
  {
    icon: faCircle,
    iconClassName: "text-aquamarine",
    title: (
      <>
        Start with the <span className="nowrap">Perfect Piece</span>
      </>
    ),
    description: "Find a design for yourself or someone you love.",
  },
  {
    icon: faDiamond,
    iconClassName: "text-peridot",
    title: (
      <>
        <span className="nowrap">Add Meaningful</span> Details
      </>
    ),
    description: "Make it unique with names, birthstones and more.",
  },
  {
    icon: faHeart,
    iconClassName: "text-pink-tourmaline",
    title: (
      <>
        Crafted <span className="nowrap">with Care</span>
      </>
    ),
    description: `Made to order by our ${jewlr_t("jewelry")} experts in North America.`,
  },
  {
    icon: faSquare,
    iconClassName: "text-citrine",
    title: (
      <>
        Ready to Keep <span className="nowrap">Or Give</span>
      </>
    ),
    description: "Arrives beautifully packaged and ready to wear.",
  },
]

const featureImageBoxElement = {
  data: { media_position: "right" },
  element_type: "feature-image-box",
  image: {
    fallback:
      "https://cms.jewlr.com/why_choose_jewlr_fpo_d_2b549d0dcd/why_choose_jewlr_fpo_d_2b549d0dcd.jpg",
    srcSet: [
      {
        height: 462,
        media: "(max-width: 1023px)",
        sizes: "100vw",
        srcSet: getImgSrcSet(
          `${sl.config.jewlr_imgsmbly_url}cms/why_choose_jewlr_fpo_m_465cca3a41/why_choose_jewlr_fpo_m_465cca3a41.jpg`,
          706
        ),
        width: 706,
      },
      {
        height: 1038,
        media: "(min-width: 1024px)",
        sizes: "(max-width: 1920px) 55vw, 990px",
        srcSet: getImgSrcSet(
          `${sl.config.jewlr_imgsmbly_url}cms/why_choose_jewlr_fpo_d_2b549d0dcd/why_choose_jewlr_fpo_d_2b549d0dcd.jpg`,
          2236
        ),
        width: 2236,
      },
    ],
  },
  style: {
    block_style: { backgroundColor: "#abe6ff" },
    container_style: {
      flexDirection: { _: "column-reverse", desktop: "row" },
    },
    text_container_style: {
      flex: "1 1 auto",
    },
    image_container_style: {
      alignItems: "center",
      display: "flex",
      flex: "0 0 55%",
    },
  },
  textarea: {
    style: {
      maxWidth: { desktop: "490px" },
    },
    texts: [
      {
        style: {
          as: "h3",
          fontSize: {
            _: "24px",
            desktop: "48px",
            large: "56px",
            widescreen: "68px",
          },
          fontWeight: 700,
          letterSpacing: { _: "0.5px", desktop: "normal" },
          lineHeight: { desktop: "110%" },
          maxWidth: { desktop: "300px", large: "420px" },
          mb: 3,
        },
        text: "Why Millions Choose Jewlr",
        type: "title",
      },
      {
        style: {
          fontSize: {
            _: "16px",
            desktop: "18px",
            large: "20px",
            widescreen: "24px",
          },
          lineHeight: {
            _: "19px",
            desktop: "22px",
            large: "24px",
            widescreen: "29px",
          },
          mb: 3,
        },
        text: `For over 15 years, we've handcrafted ${jewlr_t("terms.personalized")} ${jewlr_t("jewelry")} that celebrates life's most meaningful moments. Every piece is made to order with exceptional care and lasting quality.`,
      },
      {
        style: {
          fontSize: {
            _: "16px",
            desktop: "18px",
            large: "20px",
            widescreen: "24px",
          },
          lineHeight: {
            _: "24px",
            desktop: "29px",
            large: "34px",
            widescreen: "44px",
          },
        },
        text: "<strong>1.5M+</strong> One-of-kind designs delivered",
      },
      {
        style: {
          fontSize: {
            _: "16px",
            desktop: "18px",
            large: "20px",
            widescreen: "24px",
          },
          lineHeight: {
            _: "24px",
            desktop: "29px",
            large: "34px",
            widescreen: "44px",
          },
        },
        text: `<strong>6000+</strong> ${jewlr_t("terms.Customizable")} Styles`,
      },
      {
        style: {
          fontSize: {
            _: "16px",
            desktop: "18px",
            large: "20px",
            widescreen: "24px",
          },
          lineHeight: {
            _: "24px",
            desktop: "29px",
            large: "34px",
            widescreen: "44px",
          },
        },
        text: "<strong>1-Year</strong> Full Protection Warranty",
      },
    ],
  },
}

const b2cfeatureImageBoxElement = {
  data: { media_position: "right" },
  element_type: "feature-image-box",
  image: {
    fallback:
      "https://cms.jewlr.com/jewlr_b2c_d_fc800d4066/jewlr_b2c_d_fc800d4066.svg",
    srcSet: [
      {
        height: 327,
        media: "(max-width: 767px)",
        srcSet:
          "https://cms.jewlr.com/jewlr_b2c_m_bee43928af/jewlr_b2c_m_bee43928af.svg",
        width: 347,
      },
      {
        height: 425,
        media: "(min-width: 768px)",
        srcSet:
          "https://cms.jewlr.com/jewlr_b2c_d_fc800d4066/jewlr_b2c_d_fc800d4066.svg",
        width: 779,
      },
    ],
    style: {
      display: "block",
      maxWidth: { _: "630px", desktop: "none" },
      mx: "auto",
    },
  },
  style: {
    container_style: {
      flexDirection: { _: "column", desktop: "row" },
      height: "100%",
    },
    text_container_style: {
      flex: "1 1 auto",
    },
    image_container_style: {
      flex: "0 0 50%",
    },
  },
  textarea: {
    style: {
      display: "flex",
      justifyContent: "center",
      height: "100%",
      flexDirection: "column",
      block_style: {
        height: "100%",
      },
    },
    texts: [
      {
        style: {
          as: "h3",
          fontSize: {
            _: "24px",
            desktop: "48px",
            large: "56px",
            widescreen: "68px",
          },
          fontWeight: 700,
          letterSpacing: { _: "0.5px", desktop: "normal" },
          lineHeight: { desktop: "110%" },
          mb: 2,
          textAlign: { _: "center", desktop: "left" },
        },
        text: "Thoughtfully Made. <br/>Fairly Priced.",
        type: "title",
      },
      {
        style: {
          fontSize: {
            _: "16px",
            desktop: "18px",
            large: "20px",
            widescreen: "24px",
          },
          textAlign: { _: "center", desktop: "left" },
          lineHeight: { _: "124%", desktop: "110%" },
          mb: { _: 4, desktop: 0 },
          maxWidth: { _: "330px", desktop: "455px" },
          mx: { _: "auto", desktop: 0 },
        },
        text: "From our workshop to your doorstep, every piece skips the traditional retail markup so you get exceptional quality for less.",
      },
    ],
  },
}

const customerReviewsElement = {
  element_type: "customer-reviews",
  slider_settings: {
    speed: 600,
    autoplay: true,
    cellSpacing: "12",
    autoplayInterval: 4000,
  },
}
/* eslint-enable sort-keys */

const AboutUs = () => {
  return (
    <>
      <Helmet>
        <title>About Us</title>
      </Helmet>
      <LandingHeroBanner element={heroBannerElement} lazyload={false} />
      <div
        className="contained-wide desktop:grid-cols-4 desktop:py-48 grid grid-cols-2 gap-x-14 gap-y-36 px-16 pb-32 pt-20"
        style={{
          "--base-width": "220px",
          "--desktop-width": "250px",
        }}
      >
        {confettiRowData.map((d, index) => (
          <div
            className="grid-rows-auto grid text-center"
            key={`jewlr-experience-${index}`}
          >
            <FontAwesomeIcon
              className={cn(
                "text-28 desktop:mb-20 mb-16 w-full",
                d.iconClassName
              )}
              icon={d.icon}
            />
            <h3 className="desktop:mb-10 text-18 large:text-22 mb-6 font-bold">
              {d.title}
            </h3>
            <p className="text-16 large:text-20 max-w-(--base-width) desktop:max-w-(--desktop-width) mx-auto">
              {d.description}
            </p>
          </div>
        ))}
      </div>
      <LandingFeatureImageBox
        element={featureImageBoxElement}
        lazyload={false}
      />
      <LandingFeatureImageBox
        element={b2cfeatureImageBoxElement}
        lazyload={false}
      />
      <div className="bg-black-print widescreen:p-60 tablet:p-32 px-16 py-32 text-white">
        <div className="contained-wide">
          <h2
            className="text-24 leading-26 w-(--base-width) desktop:w-full desktop:text-34 desktop:leading-40 mx-auto mb-16 text-center font-bold capitalize"
            style={{ "--base-width": "180px" }}
          >
            Locally Crafted. Globally Loved.
          </h2>
          <p
            className="text-16 leading-20 desktop:w-(--desktop-width) desktop:text-20 desktop:leading-24 desktop:mb-32 mx-auto mb-16 w-full text-center"
            style={{ "--desktop-width": "711px" }}
          >
            Every single piece is made-to-order right here in our North American
            workshops by local artisans. By keeping our production{" "}
            {jewlr_t("terms.centralized")}, we preserve the highest standards of
            fine {jewlr_t("jewelry")} making, delivering a level of premium
            polish you can feel.
          </p>
          <div className="m-auto flex justify-center">
            <Image
              alt="US and Canada"
              className="w-(--base-width) desktop:w-(--desktop-width) mb-20"
              height="76"
              src="https://cms.jewlr.com/us_ca_white_0e699324f2/us_ca_white_0e699324f2.svg"
              style={{
                "--base-width": "71px",
                "--desktop-width": "98px",
              }}
              width="98"
            />
          </div>
          <p className="text-12 leading-14 desktop:text-16 desktop:leading-19 text-center font-semibold">
            MADE IN USA AND CANADA
          </p>
        </div>
      </div>
      <CustomerReviews element={customerReviewsElement} />
      <OccasionGrid />
    </>
  )
}

export default AboutUs
